import { Component, EventEmitter, Output, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CollabUser, CollaborationService } from '@app/services/collaboration.service';
import { AuthService } from '@app/services/auth.service';

const RESERVED_USERNAMES = [
  'pxlvd', 'admin', 'administrator', 'root', 'system', 'support', 'help',
  'staff', 'moderator', 'mod', 'superuser', 'owner', 'official', 'security',
  'bot', 'api', 'null', 'undefined', 'anonymous', 'guest', 'test', 'demo',
  'info', 'contact', 'team', 'dev', 'developer', 'me', 'you', 'user',
];

const AVATAR_EMOJIS = ['🦊','🐼','🐸','🦁','🐯','🐻','🦄','🐧','🦋','🐙','🦀','🐝','🐳','🦭','🦩','🐺'];

@Component({
  selector: 'app-user-profile-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './user-profile-modal.html',
  styleUrls: ['./user-profile-modal.css']
})
export class UserProfileModalComponent implements OnInit {
  @Input() mode: 'setup' | 'join' | 'create' = 'setup';
  @Input() prefillRoomId = '';
  @Output() confirmed = new EventEmitter<{ user: CollabUser; roomId?: string; action: 'create' | 'join' | 'save' }>();
  @Output() cancelled = new EventEmitter<void>();

  username = '';
  email = '';
  color = '#ff6b9d';
  selectedAvatar = '🦊';
  profilePicUrl: string | null = null;
  roomId = '';
  errors: Record<string, string> = {};

  avatarEmojis = AVATAR_EMOJIS;
  isLoggedIn = false;

  readonly colorOptions = [
    '#ff6b9d','#ffa94d','#69db7c','#74c0fc',
    '#da77f2','#ff8787','#63e6be','#ffd43b',
    '#a9e34b','#4dabf7','#e599f7','#ff6b6b',
  ];

  constructor(private collabSvc: CollaborationService, private auth: AuthService) {}

  ngOnInit(): void {
    this.isLoggedIn = this.auth.isAuthenticated;

    const existing = this.collabSvc.localUser;
    if (existing) {
      this.username = existing.username;
      this.email = existing.email;
      this.color = existing.color;
      this.selectedAvatar = existing.avatar;
      this.profilePicUrl = (existing as any).profilePicUrl ?? null;
    } else {
      this.color = this.collabSvc.getRandomColor();
      // Pre-fill email from auth when logged in
      if (this.isLoggedIn && this.auth.user?.email) {
        this.email = this.auth.user.email;
      }
      // Pre-fill username from auth display name
      if (this.isLoggedIn) {
        const displayName = this.auth.getDisplayName();
        if (displayName) {
          this.username = displayName.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 24);
        }
      }
    }
    if (this.prefillRoomId) {
      this.roomId = this.prefillRoomId;
    }
  }

  onProfilePicSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.errors['profilePic'] = 'Please select a valid image file.';
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      this.errors['profilePic'] = 'Image must be under 2 MB.';
      return;
    }
    delete this.errors['profilePic'];
    const reader = new FileReader();
    reader.onload = (e) => {
      this.profilePicUrl = e.target?.result as string;
      // When a custom pic is uploaded, clear emoji avatar selection
      this.selectedAvatar = '';
    };
    reader.readAsDataURL(file);
  }

  removeProfilePic(): void {
    this.profilePicUrl = null;
    this.selectedAvatar = this.avatarEmojis[0];
  }

  validate(): boolean {
    this.errors = {};
    const uname = this.username.trim();
    if (!uname) {
      this.errors['username'] = 'Username is required';
    } else if (uname.length < 2) {
      this.errors['username'] = 'At least 2 characters';
    } else if (!/^[a-z0-9_-]{2,24}$/i.test(uname)) {
      this.errors['username'] = 'Letters, numbers, _ and - only';
    } else if (RESERVED_USERNAMES.includes(uname.toLowerCase())) {
      this.errors['username'] = `"${uname}" is a reserved username`;
    }

    if (!this.isLoggedIn) {
      // Only validate email if not locked (not logged in)
      if (!this.email.trim()) this.errors['email'] = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)) this.errors['email'] = 'Invalid email';
    }

    if ((this.mode === 'join') && !this.roomId.trim()) this.errors['roomId'] = 'Room ID is required';
    return Object.keys(this.errors).length === 0;
  }

  onConfirm(): void {
    if (!this.validate()) return;
    const user: CollabUser & { profilePicUrl?: string } = {
      username: this.username.trim(),
      email: this.isLoggedIn ? (this.auth.user?.email ?? this.email.trim()) : this.email.trim(),
      color: this.color,
      avatar: this.selectedAvatar || '🦊',
      ...(this.profilePicUrl ? { profilePicUrl: this.profilePicUrl } : {}),
    };
    const action = this.mode === 'create' ? 'create' : this.mode === 'join' ? 'join' : 'save';
    this.confirmed.emit({ user, roomId: this.roomId.trim() || undefined, action });
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  generateRoomId(): void {
    this.roomId = this.collabSvc.generateRoomId();
  }
}
